import type { ReportQuestionRow } from '../../../api/types';
import { Button, PanelCard } from '../../../components/ui';

/** The n questions with the lowest % correct (questions with no data are skipped). */
export function mostMissed(rows: ReportQuestionRow[], n = 3): ReportQuestionRow[] {
  return rows
    .filter((q) => q.pct_correct != null)
    .sort((a, b) => (a.pct_correct as number) - (b.pct_correct as number))
    .slice(0, n);
}

/**
 * The one-line reason a question was missed, from its option counts and suspect reasons:
 * a key minority names the likelier answer, flat distractors say so, otherwise the most-picked
 * wrong option. Null when the question carries no option data (e.g. a written question).
 */
export function missedWhy(q: ReportQuestionRow): string | null {
  const wrong = q.options.filter((o) => !o.is_correct && o.count > 0).sort((a, b) => b.count - a.count);
  const key = q.options.find((o) => o.is_correct);
  const top = wrong[0];
  if (q.suspect_reasons.includes('key_minority') && top && key) {
    return `Most chose ${top.label} (${top.text || top.count}); the key says ${key.label}. Check the key.`;
  }
  if (q.suspect_reasons.includes('flat_distractors')) return 'Options picked about equally';
  if (top) return `${top.count} student${top.count === 1 ? '' : 's'} chose ${top.text || top.label}`;
  return null;
}

export default function MostMissedCard({ questions, suspectCount, onReviewSuspect }: {
  questions: ReportQuestionRow[]; passPct?: number; suspectCount: number; onReviewSuspect: () => void;
}) {
  const rows = mostMissed(questions);
  return (
    <PanelCard title="Most-missed questions">
      {rows.length === 0 ? (
        <p className="text-sm text-muted">No question data yet.</p>
      ) : (
        <ol>
          {rows.map((q) => {
            const why = missedWhy(q);
            return (
              <li key={q.question_id} className="flex items-start gap-3 border-b border-hairline py-[11px] first:pt-0 last:border-b-0">
                <span aria-hidden="true" className="inline-grid h-9 w-9 flex-none place-items-center rounded-[12px] bg-accent-bg font-mono text-xs font-semibold text-accent-ink">Q{q.order}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-ink" title={q.excerpt}>{`Q${q.order} · ${q.excerpt}`}</span>
                  {why && <span className="text-xs text-muted">{why}</span>}
                </span>
                <span className="font-mono font-semibold text-ink">{`${Math.round(q.pct_correct as number)}%`}</span>
              </li>
            );
          })}
        </ol>
      )}
      {suspectCount > 0 && (
        <Button variant="ghost" className="mt-3.5 w-full" onClick={onReviewSuspect}>Review suspect questions</Button>
      )}
    </PanelCard>
  );
}
